import { stripUndefined } from '@/shared/lib/stripUndefined';
import { supabase } from '@/shared/lib/supabase';
import { NotFoundError, ValidationError } from '@/shared/lib/errors';
import { logger } from '@/shared/lib/logger';
import {
  participantSchema,
  participantUpdateSchema,
  type Participant,
  type ParticipantUpdate,
} from '@shared/schemas/participants';

/**
 * Query keys jerárquicas para TanStack Query.
 * Convención: factory function para tipar y facilitar invalidación selectiva.
 */
export const profileKeys = {
  all: ['profile'] as const,
  me: () => [...profileKeys.all, 'me'] as const,
  byId: (id: string) => [...profileKeys.all, 'id', id] as const,
};

/** Obtiene el perfil del usuario autenticado actual. */
export async function fetchMyProfile(): Promise<Participant> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) {
    throw new NotFoundError('User session');
  }

  const { data, error } = await supabase
    .from('participants')
    .select('*')
    .eq('auth_user_id', userData.user.id)
    .is('deleted_at', null)
    .maybeSingle();

  if (error) {
    logger.error('fetchMyProfile failed', { code: error.code, message: error.message });
    throw new Error(error.message);
  }
  if (!data) {
    // Puede pasar durante la ventana entre signup y el trigger. Rehidratación en el siguiente refetch.
    throw new NotFoundError('Participant profile');
  }

  const parsed = participantSchema.safeParse(data);
  if (!parsed.success) {
    logger.error('fetchMyProfile: schema mismatch', { issues: parsed.error.flatten() });
    throw new Error('Perfil con formato inesperado');
  }
  return parsed.data;
}

/** Actualiza el perfil del usuario autenticado. */
export async function updateMyProfile(input: ParticipantUpdate): Promise<Participant> {
  const parsed = participantUpdateSchema.safeParse(input);
  if (!parsed.success) {
    throw new ValidationError('Datos de perfil inválidos', {
      issues: parsed.error.flatten().fieldErrors,
    });
  }

  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) {
    throw new NotFoundError('User session');
  }

  const { data, error } = await supabase
    .from('participants')
    .update(stripUndefined(parsed.data))
    .eq('auth_user_id', userData.user.id)
    .select('*')
    .single();

  if (error) {
    logger.error('updateMyProfile failed', { code: error.code, message: error.message });
    throw new Error(error.message);
  }

  const parsedResult = participantSchema.safeParse(data);
  if (!parsedResult.success) {
    throw new Error('Perfil con formato inesperado');
  }
  return parsedResult.data;
}
